<script lang="ts" setup>
import { create } from '@bufbuild/protobuf';
import { GpBtn, GpTable, GpTableColumn, GpTag, GpTooltip } from '@gportal/uikit';
import { convertToDate } from '@lib/date';
import useGpTableData from '@panel/composables/useGpTableData';
import useViewTitle from '@panel/composables/useViewTitle';
import ProjectSettingsLayout from '@panel/layouts/Layout.Project.Settings.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { CloudProviderResource } from 'gpcore-grpc/gpcore/api/cloud/v1/cloud_provider_pb';
import { ListProjectCloudProviderResourcesRequestSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/requests_pb';
import { type ListProjectCloudProviderResourcesResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

useViewTitle('Cloud provider resources');

const projectStore = useProjectStore();
const { addNotification } = useNotifications();

const isLoading = ref(false);
const pagination = reactive({ currentPage: 1, totalPages: 1 });

const route = useRoute();

const cloudProviderId = computed(() => route.params.providerId as string);

const cloudProviderResources = ref<CloudProviderResource[]>([]);

const { selectedRows: toImport, tableData } = useGpTableData(cloudProviderResources, (data) =>
  data.map((resource) => ({
    key: resource.id,
    name: resource.name,
    type: resource.type,
    nodeId: resource.nodeId,
    createdAt: convertToDate(resource.createdAt).toISOString(),
    selectable: true,
  })),
);

// Load
const reload = (page: number | Event) => {
  pagination.currentPage = page as number;

  const req = create(ListProjectCloudProviderResourcesRequestSchema, {
    cloudProviderId: cloudProviderId.value,
    projectId: projectStore.current,
    pagination: {
      page: (page as number) + 1,
    },
  });

  isLoading.value = true;
  projectStore
    .listProjectCloudProviderResources(req)
    .then((response: ListProjectCloudProviderResourcesResponse) => {
      pagination.totalPages = response.pagination?.total;
      cloudProviderResources.value = response.resources;
    })
    .catch((error: ServiceError) => {
      addNotification({
        type: 'danger',
        message: error.message,
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

// Destroy
const importModal = ref(false);
const isImporting = ref(false);
const onImport = async () => {
  if (!importModal.value) {
    return;
  }

  const promises = toImport.value.map((row) =>
    projectStore.importProjectCloudProviderResource(
      projectStore.current,
      cloudProviderId.value,
      row.key,
    ),
  );

  try {
    await Promise.all(promises);
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isImporting.value = false;
    addNotification({
      message: 'Resources has been imported successfully.',
      type: 'success',
    });
    reload(0);
  }

  importModal.value = false;
};

// Init
onMounted(() => {
  reload(0);
});
</script>

<template>
  <ProjectSettingsLayout>
    <template #buttons>
      <GpBtn
        btn-type="success"
        outline
        size="sm"
        :disabled="toImport.length === 0"
        :icon="['fas', 'fa-cloud-arrow-up']"
        label="Import"
        @click="importModal = true"
      />
    </template>

    <template #content>
      <ConfirmModal
        :show="importModal"
        confirm-text="Import"
        confirm-type="success"
        :loading="isImporting"
        @close="importModal = false"
        @confirm="onImport"
      />

      <GpTable
        :data="tableData"
        pagination
        :page="pagination.currentPage"
        :max-pages="pagination.totalPages"
        :is-loading="isLoading"
        empty-message="No cloud providers resources."
        loading-message="Loading cloud provider resources ..."
        @update:page="reload"
        v-model:selected-rows="toImport"
      >
        <GpTableColumn field="checkbox" />
        <GpTableColumn label="ID" field="key">
          <template #body="{ row }">
            <span>{{ row.key }}</span>
            <GpTooltip
              v-if="row.nodeId"
              content="This node has been imported already to your project. Click to go to the overview."
            >
              <router-link :to="{ name: 'ProjectNodeOverview', params: { id: row.nodeId } }">
                <GpTag
                  class="tw-ml-2 tw-uppercase"
                  label="Imported"
                  size="xs"
                  tag-type="success"
                  outline
                />
              </router-link>
            </GpTooltip>
          </template>
        </GpTableColumn>
        <GpTableColumn label="Name" field="name" />
        <GpTableColumn label="Flavour type" field="type" />
        <GpTableColumn label="Created at" field="createdAt" />
      </GpTable>
    </template>
  </ProjectSettingsLayout>
</template>
